import { Icon } from "@material-ui/core";
import PropTypes from "prop-types";
import React, { Component, getGlobal, setGlobal } from "reactn";

import { AssetType, LibraryView } from "common/constants";
import { ActionPermissionsObject, WorkspacePlanType } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { ds } from "js/core/models/dataService";
import { getPresentation } from "js/core/models/presentation";
import { UIActionType, isUIActionRestricted } from "js/core/uiActionsRestrictors";
import * as geom from "js/core/utilities/geom";
import { Key } from "js/core/utilities/keys";
import { trackActivity, trackActivityViaParams } from "js/core/utilities/utilities";
import { PresentationLibraryControllerInitializer } from "js/editor/PresentationLibrary/PresentationLibraryControllerInitializer";
import { PresentationLibraryPane } from "js/editor/PresentationLibrary/PresentationLibraryPane";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { Gap30 } from "js/react/components/Gap";
import ProBadge from "js/react/components/ProBadge";
import {
    BlueButton, Divider,
    UIContainer, UINavigation,
    UINavigationItem, UIPane,
    UIPaneContents, UIPaneHeader
} from "js/react/components/UiComponents";
import AISearchPane from "js/react/views/AddAssets/Panes/AISearchPane";
import IconsPane from "js/react/views/AddAssets/Panes/IconsPane";
import LoadingPane from "js/react/views/AddAssets/Panes/LoadingPane";
import LogosPane from "js/react/views/AddAssets/Panes/LogosPane";
import PresentationAssetsPane from "js/react/views/AddAssets/Panes/PresentationAssetsPane";
import RecentlyUsedAssetsPane from "js/react/views/AddAssets/Panes/RecentlyUsedAssetsPane";
import { StockPhotosPane } from "js/react/views/AddAssets/Panes/StockPhotosPane";
import { StockVideosPane } from "js/react/views/AddAssets/Panes/StockVideosPane";
import ImportMediaDialog from "js/react/views/ImportMedia/ImportMediaDialog";
import TeamAssetsPane from "js/react/views/TeamResources/panes/TeamAssetsPane";
import { $, _ } from "js/vendor";

import { UpgradePlanDialogType } from "../MarketingDialogs/UpgradePlanDialog";
import { WebImagesPane } from "./Panes/WebImagesPane";

import "css/add-assets.scss";
import { AiIconCircle } from "../../../editor/AiGeneration/AiIcon";

class AddAssetsContainer extends Component {
    lockSlide = true;

    constructor(props) {
        super(props);
        this.state = {
            isOpen: true,
            userFolders: null,
        };

        switch (props.assetType) {
            case AssetType.ICON:
                this.state.selectedPaneId = "icons";
                break;
            case AssetType.LOGO:
                this.state.selectedPaneId = "logos";
                break;
            case AssetType.VIDEO:
            case AssetType.STOCK_VIDEO:
                if (WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use) {
                    this.state.selectedPaneId = "stock-videos";
                } else {
                    this.state.selectedPaneId = "team-assets";
                }
                break;
            case AssetType.IMAGE:
            default:
                if (WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use) {
                    this.state.selectedPaneId = "stock-photos";
                } else if (WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use) {
                    this.state.selectedPaneId = "web-images";
                } else {
                    this.state.selectedPaneId = "team-assets";
                }
                break;
        }

        this.escHandler = event => {
            if (event.which == Key.ESCAPE) {
                event.stopImmediatePropagation();
                event.stopPropagation();
                event.preventDefault();
                this.handleBackButton();
                return false;
            }
        };
        // Use capture phase to catch the event before Material-UI does
        $(window).on("keydown.add-assets-container", this.escHandler);
        window.addEventListener("keydown", this.escHandler, true);

        // Clear any lingering search term when the assets container is opened
        setGlobal({ lastPhotoSearchTerm: null, lastVideoSearchTerm: null });
    }

    componentWillUnmount() {
        $(window).off("keydown.add-assets-container", this.escHandler);
        window.removeEventListener("keydown", this.escHandler, true);
    }

    handleBackButton = () => {
        if (this.state.selectedPresentationId) {
            this.setState({
                selectedPaneId: "select-presentation",
                selectedPresentationId: null
            });
            return;
        }
        this.setState({ isOpen: false });
    };

    handleCancel = () => {
        this.props.onCancel && this.props.onCancel();
    }

    closeContainer = () => {
        if (this.props.closeDialog) {
            this.props.closeDialog();
        }
    };

    processAsset = (asset, isNew) => {
        const {
            selectedPaneId,
        } = this.state;
        const {
            callback,
            bindTo = "content_value",
        } = this.props;
        let {
            type,
            fileType,
            url,
            previewUrl,
            id,
            size,
            assetProps,
            assetName,
            tags,
            hasAlpha,
        } = asset;

        if (!size) {
            size = new geom.Size(asset.get("w"), asset.get("h"));
        }

        const trackParams = {
            selectedPaneId,
        };
        if (type === AssetType.IMAGE) {
            trackParams.category = "Image";
        }
        if (type === AssetType.LOGO) {
            trackParams.category = "Logo";
        }
        if (type === AssetType.VIDEO || type === AssetType.STOCK_VIDEO) {
            trackParams.category = "Video";
        }
        trackParams.action = trackParams.action || "Added";
        trackActivityViaParams(trackParams);

        const model = {
            content_type: type,
            content_url: null,
            content_attribution: asset.get && asset.get("attribution"),
            assetProps: _.merge({}, assetProps, {
                originalSize: size,
            }),
            assetName,
            tags,
        };
        model[bindTo] = id;

        const isGeneratedByAi = !!(asset.get && asset.get("isGenerated")) || asset.isGenerated || assetProps?.isGenerated;
        if (isGeneratedByAi) {
            const aiImageStyle = (asset.get && asset.get("aiImageStyle")) || asset.aiImageStyle || assetProps?.aiImageStyle;
            const aiPrompt = (asset.get && asset.get("attribution")) || asset.attribution || assetName;
            const aiImageModel = (asset.get && asset.get("source")) || asset.source || assetProps?.source;

            model.__generateImage = {
                search: typeof aiPrompt === "string" ? aiPrompt : "",
                imageStyle: aiImageStyle || "minimalist",
                imageSource: "ai",
                imageModel: aiImageModel,
                isGenerating: false,
                isSlideImage: false
            };
        } else {
            model.__generateImage = null;
        }

        if (type === AssetType.STOCK_VIDEO) {
            model.content_url = url;
            model.previewUrl = previewUrl;
        }

        // If we have a transparent image, remove the background color
        if (
            (
                type === AssetType.IMAGE ||
                type === AssetType.LOGO
            ) &&
            fileType !== "svg" &&
            hasAlpha
        ) {
            model.cellColor = "none";
        }

        callback(model, isNew, asset);
    }

    handleSelectNewAsset = asset => {
        if (this.state.isAlreadyClicked) return;
        this.setState({
            isAlreadyClicked: true
        });
        this.processAsset(asset, true);
    };

    handleSelectExistingAsset = asset => {
        if (this.state.isAlreadyClicked) return;
        this.setState({
            isAlreadyClicked: true
        });
        this.processAsset(asset, false);
    };

    handleSelectExistingTeamAsset = async asset => {
        if (this.state.isAlreadyClicked) return;
        this.setState({
            isAlreadyClicked: true
        });
        const assetModel = await ds.assets.getAssetById(asset.resourceId);
        this.processAsset(assetModel, false);
    };

    importAsset = async () => {
        const { backgroundVideoOnly = true, hideVideos, assetType, isRootElementCallout } = this.props;
        const { selectedPaneId } = this.state;

        ShowDialog(ImportMediaDialog, {
            fileTypes: !hideVideos ? ["image", "video"] : ["image"],
            imageAsLogo: selectedPaneId === "logos",
            backgroundVideoOnly,
            onSuccess: asset => {
                // If we upload a video as a background,
                // for an asset type that is not a video,
                // we want to set it to loop and autoplay
                // or We want to force autoplay and loop for videos in callouts
                // that are not set to be a background video
                const setLoopAndAutoPlay = assetType !== AssetType.VIDEO &&
                    assetType !== AssetType.STOCK_VIDEO &&
                    asset.type === AssetType.VIDEO || asset.type === AssetType.VIDEO && isRootElementCallout;

                if (setLoopAndAutoPlay) {
                    asset.assetProps = {
                        ...asset.assetProps,
                        autoPlay: true,
                        loop: true
                    };
                }

                this.processAsset(asset, true);
                this.closeContainer();
            }
        });
    }

    handleCopyAssetPresentationClicked = async presentationId => {
        const selectedPresentation = await getPresentation(presentationId, { permission: "read", autoSync: false });

        this.setState({
            selectedPaneId: "select-presentation-asset",
            selectedPresentation
        });
    };

    deselectUserFolder = () => {
        this.setState({
            selectedSecondaryId: null
        });
    };

    renderSelectedPane = () => {
        let {
            selectedPaneId,
            selectedPresentation,
            prefilledSearchQuery,
        } = this.state;
        const {
            backgroundVideoOnly = true,
            hideVideos,
            hidePhotos,
            hideLogos,
        } = this.props;

        const { lastLogoSearchTerm } = getGlobal();
        if (selectedPaneId === "logos" && lastLogoSearchTerm) {
            selectedPaneId = "search-logos";
        }

        const assetTypes = [
            !hidePhotos && AssetType.IMAGE,
            !hideLogos && AssetType.LOGO,
            !hideVideos && AssetType.VIDEO,
            !hideVideos && AssetType.STOCK_VIDEO
        ].filter(val => val);

        switch (selectedPaneId) {
            case "recently-used-media":
                return (
                    <RecentlyUsedAssetsPane
                        key="recently-used-media"
                        assetTypes={assetTypes}
                        showSearch={false}
                        backgroundVideoOnly={backgroundVideoOnly}
                        workspaceId={AppController.workspaceId}
                        addAssetCallback={this.handleSelectExistingAsset}
                        handleConfirm={this.closeContainer}
                        handleSearch={query => {
                            this.setState({
                                selectedPaneId: AssetType.IMAGE,
                                selectedSecondaryId: "stock_photos",
                                prefilledSearchQuery: query
                            });
                        }}
                    />
                );
            case "icons":
                return (
                    <IconsPane
                        addAssetCallback={this.handleSelectExistingAsset}
                        handleConfirm={this.closeContainer}
                        workspaceId={AppController.workspaceId}
                        searchType="icons"
                    />
                );
            case "logos":
                return (
                    <RecentlyUsedAssetsPane
                        key="logos"
                        assetTypes={[AssetType.LOGO]}
                        showSearch={true}
                        searchPrompt="Search for logos..."
                        addAssetCallback={this.handleSelectExistingAsset}
                        handleConfirm={this.closeContainer}
                        handleSearch={query => {
                            this.setState({
                                selectedPaneId: "search-logos",
                                prefilledSearchQuery: query
                            });
                        }}
                    />
                );
            case "ai-search":
                return (
                    <AISearchPane
                        key="generate"
                        allowSearch
                        addAssetCallback={this.handleSelectExistingAsset}
                        handleConfirm={this.closeContainer}
                    />
                );
            case "stock-photos":
                return (
                    <StockPhotosPane
                        addAssetCallback={this.handleSelectNewAsset}
                        handleConfirm={this.closeContainer}
                        prefilledSearchQuery={prefilledSearchQuery}
                        onPrefilledSearchQueryComplete={() => {
                            this.setState({
                                prefilledSearchQuery: null
                            });
                        }}
                    />
                );
            case "stock-videos":
                return (
                    <StockVideosPane
                        addAssetCallback={this.handleSelectNewAsset}
                        handleConfirm={this.closeContainer}
                        prefilledSearchQuery={prefilledSearchQuery}
                        onPrefilledSearchQueryComplete={() => {
                            this.setState({
                                prefilledSearchQuery: null
                            });
                        }}
                    />
                );
            case "web-images":
                return (
                    <WebImagesPane
                        addAssetCallback={this.handleSelectNewAsset}
                        handleConfirm={this.closeContainer}
                        prefilledSearchQuery={prefilledSearchQuery}
                        onPrefilledSearchQueryComplete={() => {
                            this.setState({
                                prefilledSearchQuery: null
                            });
                        }}
                    />
                );
            case "search-logos":
                return (
                    <LogosPane
                        addAssetCallback={this.handleSelectNewAsset}
                        handleConfirm={this.closeContainer}
                        prefilledSearchQuery={prefilledSearchQuery}
                        resetPreviouslyViewedState={() => {
                            this.setState({ selectedPaneId: "logos" });
                            this.renderSelectedPane();
                        }}
                        onPrefilledSearchQueryComplete={() => {
                            this.setState({
                                prefilledSearchQuery: null
                            });
                        }}
                    />
                );
            case "select-presentation":
                return (
                    <UIPane>
                        <UIPaneHeader>Choose a presentation to copy assets from</UIPaneHeader>
                        <UIPaneContents>
                            <PresentationLibraryControllerInitializer
                                view={LibraryView.LIBRARY}
                                pane={"recent"}
                            >
                                <PresentationLibraryPane
                                    showLibraryViewSelect={false}
                                    workspaceId={AppController.workspaceId}
                                    readOnly
                                    showWorkspaceChooser={false}
                                    onSelectedPresentation={this.handleCopyAssetPresentationClicked}
                                    onDoubleClick={() => { }} // Prevent default behaviour (navigate to presentation), fixes BA-16140
                                />
                            </PresentationLibraryControllerInitializer>
                        </UIPaneContents>
                    </UIPane>
                );
            case "select-presentation-asset":
                return (
                    <PresentationAssetsPane
                        assetTypes={assetTypes}
                        addAssetCallback={this.handleSelectExistingAsset}
                        handleConfirm={this.closeContainer}
                        selectedPresentation={selectedPresentation}
                        backgroundVideoOnly={backgroundVideoOnly}
                    />
                );
            case "team-assets":
                return (
                    <TeamAssetsPane
                        addAssetCallback={this.handleSelectExistingTeamAsset}
                        handleConfirm={this.closeContainer}
                    />
                );
            default:
                return (<LoadingPane />);
        }
    };

    render() {
        const { hideTeamAssets, hideIcons: iconsHidden, hideVideos: videosHidden, hidePhotos: photosHidden, hideLogos: logosHidden, hideAISearch: aiSearchHidden } = this.props;
        const { isOpen, selectedPaneId } = this.state;

        const workspaceId = AppController.workspaceId;

        const assetRestrictions = {
            hideIcons: iconsHidden || isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.STOCK_VIDEO }),
            hideVideos: videosHidden || isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.STOCK_VIDEO }),
            hidePhotos: photosHidden || isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.IMAGE }),
            hideLogos: logosHidden || isUIActionRestricted(UIActionType.ADD_ASSET, { assetType: AssetType.LOGO }),
            hideAISearch: aiSearchHidden || !WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use || !WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use
        };

        const { hideIcons, hideVideos, hidePhotos, hideLogos, hideAISearch } = assetRestrictions;

        return (
            <UIContainer
                isOpen={isOpen}
                className="add-asset-container"
                onClose={() => {
                    this.closeContainer();
                    this.handleCancel();
                }}
            >
                <UINavigation
                    selected={selectedPaneId}
                    onSelect={id => {
                        this.setState({ selectedPaneId: id });

                        let props = {
                            selectedPaneId,
                        };

                        trackActivity("Image", "PaneSelected", null, null, props, { audit: false, skipAmplitude: true });
                    }}
                    onBack={this.handleBackButton}
                >
                    <BlueButton onClick={e => this.importAsset()}>
                        <Icon>cloud_upload</Icon>Import...
                    </BlueButton>
                    <Gap30 />
                    {
                        !hideTeamAssets &&
                        <UINavigationItem
                            id="team-assets"
                            title="Team Assets"
                            icon={!WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "use") && "group"}
                            image={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "use") && <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} />}
                        />
                    }
                    <UINavigationItem
                        id="recently-used-media"
                        title="Recent Media"
                        icon="star_border"
                    />
                    <UINavigationItem
                        id="select-presentation"
                        title="Find in slides"
                        icon="search"
                    />
                    <Divider color="#444" margin={20} />
                    {
                        !hidePhotos &&
                        WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use &&
                        <UINavigationItem
                            id="stock-photos"
                            title="Stock Images"
                            icon="camera_alt"
                        />
                    }
                    {
                        !hidePhotos &&
                        WorkspaceController.actionPermissions[ActionPermissionsObject.WEB_IMAGES].use &&
                        <UINavigationItem
                            id="web-images"
                            title="Web Images"
                            icon="language"
                        />
                    }
                    {
                        !hideVideos &&
                        WorkspaceController.actionPermissions[ActionPermissionsObject.STOCK_LIBRARIES].use &&
                        <UINavigationItem
                            id="stock-videos"
                            title="Stock Videos"
                            icon="video_library"
                        />
                    }
                    {!hideIcons && (
                        <UINavigationItem
                            id="icons"
                            title="Icons"
                            icon="filter_vintage"
                        />
                    )}
                    {!hideLogos && (
                        <UINavigationItem
                            id={selectedPaneId !== "search-logos" ? "logos" : "search-logos"}
                            title="Logos"
                            icon="sort_by_alpha"
                        />
                    )}
                    {!hideAISearch && <Divider color="#444" margin={20} />}
                    {!hideAISearch && (
                        <UINavigationItem
                            id="ai-search"
                            title="CREATE WITH AI..."
                            image={WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, "use") ? <UpgradeIcon style={{ width: 24, height: 24, margin: 0 }} /> : <AiIconCircle />}
                        />
                    )}
                </UINavigation>
                {this.renderSelectedPane()}
            </UIContainer >
        );
    }
}

AddAssetsContainer.propTypes = {
    assetType: PropTypes.string,
    callback: PropTypes.func
};

export default AddAssetsContainer;
